"use client"

import { useState, useRef, useEffect } from "react"
import { X, ChevronDown, Search } from "lucide-react"
import { naicsCodes } from "../search/FilterValues"

import { cn } from "@/lib/utils"

export function NaicsCodeSelector({ value = [], onChange, disabled = false, placeholder = "Select NAICS codes..." }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleOutsideClick)
    return () => document.removeEventListener("mousedown", handleOutsideClick)
  }, [])

  const filteredCodes = naicsCodes
    .filter((code) => {
      // For numeric search, match from the beginning of the code (startsWith)
      if (/^\d+$/.test(searchQuery)) {
        return code.code.toString().startsWith(searchQuery)
      }
      // For text search, match anywhere in the name (includes)
      return code.name.toLowerCase().includes(searchQuery.toLowerCase())
    })
    .slice(0, 10)

  const isCodeSelected = (code) => value.includes(code.toString())

  const addCode = (code) => {
    if (!isCodeSelected(code) && !disabled) {
      onChange([...value, code.toString()])
    }
    setSearchQuery("")
    inputRef.current?.focus()
  }

  const removeCode = (code) => {
    if (!disabled) {
      onChange(value.filter((c) => c !== code))
    }
  }

  const getNameForCode = (code) => {
    const foundCode = naicsCodes.find((c) => c.code.toString() === code)
    return foundCode ? foundCode.name : code
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={cn(
          "min-h-10 p-1 border rounded-md flex flex-wrap gap-1 cursor-text transition-colors",
          isOpen ? "border-primary ring-1 ring-ring" : "border-input",
          disabled ? "bg-muted opacity-50 cursor-not-allowed" : "bg-background",
        )}
        onClick={() => {
          if (!disabled) {
            setIsOpen(true)
            inputRef.current?.focus()
          }
        }}
      >
        {value.length > 0 &&
          value.map((code) => (
            <div
              key={code}
              className="inline-flex items-center bg-secondary text-secondary-foreground rounded-md px-2 py-1 text-sm"
            >
              <span className="font-medium mr-1">{code}</span>
              <span className="truncate max-w-[200px]">{getNameForCode(code)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="ml-1 rounded-full hover:bg-secondary/80"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeCode(code)
                  }}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}

        <div className="flex-1 inline-flex items-center">
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            disabled={disabled}
            className={cn(
              "flex-1 bg-transparent outline-none border-none p-1 text-sm placeholder:text-muted-foreground",
              value.length > 0 ? "w-auto" : "w-full",
            )}
            placeholder={value.length > 0 ? "" : placeholder}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsOpen(true)}
          />
        </div>

        <div className="self-center ml-auto pl-2 flex-shrink-0">
          <button
            type="button"
            className={cn(
              "p-1 rounded hover:bg-secondary transition-colors",
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            )}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) setIsOpen(!isOpen)
            }}
          >
            <ChevronDown className="h-4 w-4 opacity-50" />
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-popover text-popover-foreground shadow-md rounded-md border overflow-hidden">
          <div className="p-2 border-b flex items-center gap-2">
            <Search className="h-4 w-4 opacity-50" />
            <input
              autoFocus
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search code or name..."
              className="bg-transparent outline-none flex-1 text-sm"
            />
          </div>

          <div className="max-h-60 overflow-y-auto p-1">
            {filteredCodes.length > 0 ? (
              filteredCodes.map((code) => (
                <div
                  key={code.code}
                  className={cn(
                    "flex items-center p-2 hover:bg-secondary/50 rounded-sm cursor-pointer text-sm",
                    isCodeSelected(code.code) ? "bg-secondary" : "",
                  )}
                  onClick={() => addCode(code.code)}
                >
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span className="font-medium mr-2">{code.code}</span>
                      <span>{code.name}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-2 text-center text-muted-foreground text-sm">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

